import React from 'react';
import ConstructionLayout from './ConstructionLayout';
import { useTheme } from '../../contexts/ThemeContext';
import { useModule } from '../../contexts/ModuleContext';
import { Calendar, ArrowRight, Building2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function ProgressPage() {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const { activeProject } = useModule();

  const workPackages = [
    { name: 'Excavation & Earthwork', progress: 85, color: '#10B981' },
    { name: 'Foundation & Substructure', progress: 70, color: '#3B82F6' },
    { name: 'Superstructure & Framing', progress: 45, color: '#F59E0B' },
    { name: 'MEP Services', progress: 20, color: '#A855F7' },
  ];

  const highlights = [
    'Excavation in Block A completed ahead of target schedule.',
    'Stockpile volume decreased by 12% following backfilling operations.',
    '2 High Severity structural observations logged during latest survey.',
  ];

  return (
    <ConstructionLayout title="Progress">
      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {/* Header */}
        <div>
          <h2 style={{ fontSize: '22px', fontWeight: '700', color: isDarkMode ? '#F3F4F6' : '#111827', margin: 0 }}>Progress Overview</h2>
          <p style={{ fontSize: '13px', color: isDarkMode ? '#9CA3AF' : '#6B7280', margin: '4px 0 0 0' }}>
            Overall project progress and site update from latest survey
          </p>
        </div>

        {/* Top 4 KPI Metrics Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
          {/* Card 1: Overall Progress */}
          <div style={{ backgroundColor: isDarkMode ? '#111622' : '#FFFFFF', border: `1px solid ${isDarkMode ? '#1E2433' : '#E5E7EB'}`, borderRadius: '12px', padding: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <span style={{ fontSize: '11px', color: isDarkMode ? '#9CA3AF' : '#6B7280' }}>Overall Progress</span>
              <div style={{ fontSize: '24px', fontWeight: 800, color: '#10B981', margin: '4px 0' }}>64.8%</div>
              <span style={{ fontSize: '11px', color: '#10B981' }}>↑ 5.6% vs last survey</span>
            </div>
            <div style={{ width: '48px', height: '48px', borderRadius: '50%', border: '4px solid #10B981', display: 'flex', alignItems: 'center', justifyContent: 'center' }} />
          </div>

          {/* Card 2: Planned Progress */}
          <div style={{ backgroundColor: isDarkMode ? '#111622' : '#FFFFFF', border: `1px solid ${isDarkMode ? '#1E2433' : '#E5E7EB'}`, borderRadius: '12px', padding: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <span style={{ fontSize: '11px', color: isDarkMode ? '#9CA3AF' : '#6B7280' }}>Planned Progress</span>
              <div style={{ fontSize: '24px', fontWeight: 800, color: '#3B82F6', margin: '4px 0' }}>61.2%</div>
              <span style={{ fontSize: '11px', color: isDarkMode ? '#9CA3AF' : '#6B7280' }}>As per baseline schedule</span>
            </div>
            <div style={{ width: '48px', height: '48px', borderRadius: '50%', border: '4px solid #3B82F6', display: 'flex', alignItems: 'center', justifyContent: 'center' }} />
          </div>

          {/* Card 3: Variance */}
          <div style={{ backgroundColor: isDarkMode ? '#111622' : '#FFFFFF', border: `1px solid ${isDarkMode ? '#1E2433' : '#E5E7EB'}`, borderRadius: '12px', padding: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <span style={{ fontSize: '11px', color: isDarkMode ? '#9CA3AF' : '#6B7280' }}>Progress Variance</span>
              <div style={{ fontSize: '24px', fontWeight: 800, color: '#A855F7', margin: '4px 0' }}>+3.6%</div>
              <span style={{ fontSize: '11px', color: '#A855F7' }}>Ahead of plan</span>
            </div>
            <div style={{ width: '48px', height: '48px', borderRadius: '50%', border: '4px solid #A855F7', display: 'flex', alignItems: 'center', justifyContent: 'center' }} />
          </div>

          {/* Card 4: Last Survey */}
          <div style={{ backgroundColor: isDarkMode ? '#111622' : '#FFFFFF', border: `1px solid ${isDarkMode ? '#1E2433' : '#E5E7EB'}`, borderRadius: '12px', padding: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <span style={{ fontSize: '11px', color: isDarkMode ? '#9CA3AF' : '#6B7280' }}>Last Survey</span>
              <div style={{ fontSize: '15px', fontWeight: 700, color: isDarkMode ? '#F3F4F6' : '#111827', margin: '8px 0 4px 0' }}>24 Aug 2024</div>
              <span style={{ fontSize: '11px', color: isDarkMode ? '#9CA3AF' : '#6B7280' }}>06:45 PM</span>
            </div>
            <div style={{ width: '40px', height: '40px', borderRadius: '8px', backgroundColor: isDarkMode ? '#161B26' : '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Calendar size={18} color="#0082F8" />
            </div>
          </div>
        </div>

        {/* Main Grid: Visual Progress vs Work Package + Right Sidebar */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: '20px' }}>
          {/* Left Column Section */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {/* Middle Section: Chart + Work Packages */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              {/* Progress Graph Card */}
              <div style={{ backgroundColor: isDarkMode ? '#111622' : '#FFFFFF', border: `1px solid ${isDarkMode ? '#1E2433' : '#E5E7EB'}`, borderRadius: '12px', padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ fontSize: '14px', fontWeight: 700, color: isDarkMode ? '#F3F4F6' : '#111827' }}>
                  Visual Progress vs Planned Progress
                </div>
                
                {/* SVG Line Graph */}
                <div style={{ height: '180px', position: 'relative', marginTop: '10px' }}>
                  <svg width="100%" height="100%" viewBox="0 0 300 150" preserveAspectRatio="none">
                    {/* Grid lines */}
                    <line x1="0" y1="30" x2="300" y2="30" stroke={isDarkMode ? "#1E2433" : "#E5E7EB"} strokeDasharray="3 3" />
                    <line x1="0" y1="70" x2="300" y2="70" stroke={isDarkMode ? "#1E2433" : "#E5E7EB"} strokeDasharray="3 3" />
                    <line x1="0" y1="110" x2="300" y2="110" stroke={isDarkMode ? "#1E2433" : "#E5E7EB"} strokeDasharray="3 3" />

                    {/* Planned line (blue dashed) */}
                    <polyline fill="none" stroke="#3B82F6" strokeWidth="2" strokeDasharray="4 4" points="10,130 60,110 120,90 180,75 240,60 290,45" />

                    {/* Visual line (green solid) */}
                    <polyline fill="none" stroke="#10B981" strokeWidth="3" points="10,135 60,105 120,82 180,65 240,50 290,38" />

                    {/* Data dots */}
                    <circle cx="290" cy="38" r="4" fill="#10B981" />
                    <circle cx="290" cy="45" r="4" fill="#3B82F6" />
                  </svg>
                </div>

                <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', fontSize: '11px' }}>
                  <span style={{ color: '#10B981', fontWeight: 600 }}>● Visual Progress (64.8%)</span>
                  <span style={{ color: '#3B82F6', fontWeight: 600 }}>-- Planned Progress (61.2%)</span>
                </div>
              </div>

              {/* Progress by Work Package Card */}
              <div style={{ backgroundColor: isDarkMode ? '#111622' : '#FFFFFF', border: `1px solid ${isDarkMode ? '#1E2433' : '#E5E7EB'}`, borderRadius: '12px', padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ fontSize: '14px', fontWeight: 700, color: isDarkMode ? '#F3F4F6' : '#111827' }}>
                  Progress by Work Package
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {workPackages.map((wp, idx) => (
                    <div key={idx} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px' }}>
                        <span style={{ color: isDarkMode ? '#E5E7EB' : '#374151' }}>{wp.name}</span>
                        <strong style={{ color: wp.color }}>{wp.progress}%</strong>
                      </div>
                      <div style={{ height: '6px', backgroundColor: isDarkMode ? '#161B26' : '#E5E7EB', borderRadius: '3px', overflow: 'hidden' }}>
                        <div style={{ width: `${wp.progress}%`, height: '100%', backgroundColor: wp.color, borderRadius: '3px' }} />
                      </div>
                    </div>
                  ))}
                </div>

                <button style={{ marginTop: 'auto', background: 'none', border: 'none', color: '#0082F8', fontSize: '12px', fontWeight: 600, cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  View all work packages <ArrowRight size={12} />
                </button>
              </div>
            </div>

            {/* Bottom Row: High Priority Issues + Site Header */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 240px', gap: '20px' }}>
              {/* High Priority Observations Table */}
              <div style={{ backgroundColor: isDarkMode ? '#111622' : '#FFFFFF', border: `1px solid ${isDarkMode ? '#1E2433' : '#E5E7EB'}`, borderRadius: '12px', padding: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div style={{ fontSize: '14px', fontWeight: 700, color: isDarkMode ? '#F3F4F6' : '#111827' }}>
                  Site Observations & Issues (High Priority)
                </div>

                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', textAlign: 'left' }}>
                    <thead>
                      <tr style={{ color: isDarkMode ? '#9CA3AF' : '#6B7280', borderBottom: `1px solid ${isDarkMode ? '#1E2433' : '#E5E7EB'}` }}>
                        <th style={{ padding: '8px' }}>ID</th>
                        <th style={{ padding: '8px' }}>Observation</th>
                        <th style={{ padding: '8px' }}>Category</th>
                        <th style={{ padding: '8px' }}>Severity</th>
                        <th style={{ padding: '8px' }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr style={{ borderBottom: `1px solid ${isDarkMode ? '#1E2433' : '#F3F4F6'}` }}>
                        <td style={{ padding: '8px', color: '#EF4444', fontWeight: 700 }}>62</td>
                        <td style={{ padding: '8px' }}>Column at grid L-15/16 missing</td>
                        <td style={{ padding: '8px', color: '#C084FC' }}>Structural</td>
                        <td style={{ padding: '8px', color: '#EF4444' }}>High</td>
                        <td style={{ padding: '8px', color: '#EF4444' }}>Open</td>
                      </tr>
                      <tr style={{ borderBottom: `1px solid ${isDarkMode ? '#1E2433' : '#F3F4F6'}` }}>
                        <td style={{ padding: '8px', color: '#EF4444', fontWeight: 700 }}>63</td>
                        <td style={{ padding: '8px' }}>Column at grid H-15/16 missing</td>
                        <td style={{ padding: '8px', color: '#C084FC' }}>Structural</td>
                        <td style={{ padding: '8px', color: '#EF4444' }}>High</td>
                        <td style={{ padding: '8px', color: '#EF4444' }}>Open</td>
                      </tr>
                      <tr style={{ borderBottom: `1px solid ${isDarkMode ? '#1E2433' : '#F3F4F6'}` }}>
                        <td style={{ padding: '8px', color: '#EF4444', fontWeight: 700 }}>65</td>
                        <td style={{ padding: '8px' }}>Loose electrical cable lying on ground</td>
                        <td style={{ padding: '8px', color: '#FBBF24' }}>Safety</td>
                        <td style={{ padding: '8px', color: '#EF4444' }}>High</td>
                        <td style={{ padding: '8px', color: '#EF4444' }}>Open</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <button onClick={() => navigate('/construction/observations')} style={{ background: 'none', border: 'none', color: '#0082F8', fontSize: '12px', fontWeight: 600, cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '4px', marginTop: '6px' }}>
                  View all observations <ArrowRight size={12} />
                </button>
              </div>

              {/* Site Project Details */}
              <div style={{ backgroundColor: isDarkMode ? '#111622' : '#FFFFFF', border: `1px solid ${isDarkMode ? '#1E2433' : '#E5E7EB'}`, borderRadius: '12px', padding: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div style={{ fontSize: '12px', fontWeight: 700, color: isDarkMode ? '#F3F4F6' : '#111827' }}>
                  Selected Project
                </div>
                
                <div style={{ height: '120px', borderRadius: '8px', backgroundColor: isDarkMode ? '#161B26' : '#F0F4F8', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8px', color: '#0082F8' }}>
                  <Building2 size={32} />
                  <span style={{ fontSize: '12px', fontWeight: 700, color: isDarkMode ? '#F3F4F6' : '#111827' }}>
                    {activeProject?.name || 'Embassy Eden – F1'}
                  </span>
                </div>

                <button onClick={() => navigate('/construction/visuals')} style={{ background: 'none', border: 'none', color: '#0082F8', fontSize: '11px', fontWeight: 600, cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  Go to Site Visuals <ArrowRight size={12} />
                </button>
              </div>
            </div>
          </div>

          {/* Right Column Sidebar: Key Highlights + Summary */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Key Highlights Card */}
            <div style={{ backgroundColor: isDarkMode ? '#111622' : '#FFFFFF', border: `1px solid ${isDarkMode ? '#1E2433' : '#E5E7EB'}`, borderRadius: '12px', padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ fontSize: '14px', fontWeight: 700, color: isDarkMode ? '#F3F4F6' : '#111827' }}>
                Key Highlights (vs Previous)
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '12px' }}>
                {highlights.map((h, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', color: isDarkMode ? '#E5E7EB' : '#374151' }}>
                    <span style={{ color: i === 2 ? '#EF4444' : '#10B981', fontWeight: 'bold' }}>●</span>
                    <span>{h}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </ConstructionLayout>
  );
}
